/**
 * API client (M01).
 *
 * Every backend response is the envelope `{data, error, request_id}` (docs/api-contracts.md §1),
 * so unwrapping and error mapping happen exactly once, here. Callers get the payload or an
 * {@link ApiError} carrying the machine-readable `code` — screens branch on the code, never on
 * a message string, because messages are safe prose and may change.
 */

export const API_BASE = '/api/v1'

export interface Envelope<T> {
  data: T | null
  error: ApiErrorBody | null
  request_id: string
}

export interface ApiErrorBody {
  code: string
  message: string
  details?: Array<{ field: string; issue: string }>
}

/** Error codes the SPA branches on. Mirrors `app/errors.py::ErrorCode`. */
export const ErrorCodes = {
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  REQUEST_TOO_LARGE: 'REQUEST_TOO_LARGE',
  INVALID_OPERATION: 'INVALID_OPERATION',
  AUTH_REQUIRED: 'AUTH_REQUIRED',
  AUTH_INVALID: 'AUTH_INVALID',
  FORBIDDEN_ROLE: 'FORBIDDEN_ROLE',
  CSRF_INVALID: 'CSRF_INVALID',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  /** A legacy participant's password cannot be recovered; an admin reset is the remedy. */
  CREDENTIAL_RESET_REQUIRED: 'CREDENTIAL_RESET_REQUIRED',
  /** Participant login refused during the five-minute lock (S1 §6.2). */
  ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',
  PROJECT_NOT_SELECTABLE: 'PROJECT_NOT_SELECTABLE',
  AUTH_WINDOW_EXPIRED: 'AUTH_WINDOW_EXPIRED',
  CONSENT_REQUIRED: 'CONSENT_REQUIRED',
  SESSION_STATE_INVALID: 'SESSION_STATE_INVALID',
  SEQUENCE_INVALID: 'SEQUENCE_INVALID',
  CHUNK_COVERAGE_INCOMPLETE: 'CHUNK_COVERAGE_INCOMPLETE',
  PROCESSING_ACTIVE: 'PROCESSING_ACTIVE',
  IMPORT_ROWS_INVALID: 'IMPORT_ROWS_INVALID',
  CONFIG_LIMIT_EXCEEDED: 'CONFIG_LIMIT_EXCEEDED',
  RATE_LIMITED: 'RATE_LIMITED',
  DATABASE_UNAVAILABLE: 'DATABASE_UNAVAILABLE',
  STORAGE_UNAVAILABLE: 'STORAGE_UNAVAILABLE',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
} as const

export type ErrorCode = (typeof ErrorCodes)[keyof typeof ErrorCodes]

export class ApiError extends Error {
  readonly code: string
  readonly status: number
  readonly requestId: string
  readonly details: Array<{ field: string; issue: string }>
  readonly retryAfterSeconds?: number

  constructor(args: {
    code: string
    message: string
    status: number
    requestId: string
    details?: Array<{ field: string; issue: string }>
    retryAfterSeconds?: number
  }) {
    super(args.message)
    this.name = 'ApiError'
    this.code = args.code
    this.status = args.status
    this.requestId = args.requestId
    this.details = args.details ?? []
    this.retryAfterSeconds = args.retryAfterSeconds
  }

  /** True when retrying the same request could plausibly succeed. */
  get isTransient(): boolean {
    return this.status === 503 || this.status === 429
  }

  /** Field-level issues keyed by field name, for form rendering. */
  fieldIssues(): Record<string, string> {
    return Object.fromEntries(this.details.map((detail) => [detail.field, detail.issue]))
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  /** Bearer token for participant endpoints; admin endpoints use the session cookie instead. */
  accessToken?: string
  /** Double-submit CSRF token for state-changing admin requests (SEC-03). */
  csrfToken?: string
  /** Correlation id to reuse across a retry chain so the server logs tie together. */
  requestId?: string
  signal?: AbortSignal
  headers?: Record<string, string>
}

/**
 * Broadcasts that an *admin* request came back unauthenticated after the session was believed
 * live — a background query on a settings/reports/etc. screen hit a `401`, not the initial
 * `/admin/me` probe on mount. `useAdminSession` is the sole subscriber: it resets to "no session"
 * so `AdminApp` can distinguish an expired session from the page's very first load and reset the
 * route centrally, instead of every screen guessing for itself (docs/design.md §auth).
 */
type AdminAuthLostListener = () => void
const adminAuthLostListeners = new Set<AdminAuthLostListener>()

export function onAdminAuthLost(listener: AdminAuthLostListener): () => void {
  adminAuthLostListeners.add(listener)
  return () => adminAuthLostListeners.delete(listener)
}

function notifyAdminAuthLost(): void {
  for (const listener of adminAuthLostListeners) listener()
}

/** Paths excluded from the broadcast: an expected, not a lost, authentication state. */
function isAdminAuthLostSignal(path: string, code: string): boolean {
  if (code !== ErrorCodes.AUTH_REQUIRED && code !== ErrorCodes.AUTH_INVALID) return false
  if (!path.startsWith('/admin')) return false
  // A failed sign-in attempt is not a lost session — there was never one to lose.
  if (path === '/admin/login') return false
  return true
}

let requestCounter = 0

/** Generates a client-side correlation id. `crypto.randomUUID` is not available over plain HTTP. */
export function newRequestId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID().replace(/-/g, '')
  }
  requestCounter += 1
  return `c${Date.now().toString(36)}${requestCounter.toString(36)}`
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const requestId = options.requestId ?? newRequestId()
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Request-ID': requestId,
    ...options.headers,
  }
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'
  if (options.accessToken) headers.Authorization = `Bearer ${options.accessToken}`
  if (options.csrfToken) headers['X-CSRF-Token'] = options.csrfToken

  const response = await fetch(`${API_BASE}${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    // Admin auth is a cookie; participant refresh is a cookie. Same-origin only.
    credentials: 'same-origin',
    signal: options.signal,
  })

  if (response.status === 204) return undefined as T

  let envelope: Envelope<T> | null = null
  try {
    envelope = (await response.json()) as Envelope<T>
  } catch {
    // A non-JSON body means something upstream of the API answered (proxy, gateway). Treat it
    // as an infrastructure failure rather than surfacing an unparseable payload to a screen.
    throw new ApiError({
      code: ErrorCodes.SERVICE_UNAVAILABLE,
      message: 'The service returned an unreadable response',
      status: response.status,
      requestId: response.headers.get('X-Request-ID') ?? requestId,
    })
  }

  const responseRequestId = envelope?.request_id || response.headers.get('X-Request-ID') || requestId

  if (!response.ok || envelope?.error) {
    const retryAfter = response.headers.get('Retry-After')
    const code = envelope?.error?.code ?? ErrorCodes.INTERNAL_ERROR
    if (isAdminAuthLostSignal(path, code)) notifyAdminAuthLost()
    throw new ApiError({
      code,
      message: envelope?.error?.message ?? 'Request failed',
      status: response.status,
      requestId: responseRequestId,
      details: envelope?.error?.details,
      retryAfterSeconds: retryAfter ? Number(retryAfter) : undefined,
    })
  }

  return envelope.data as T
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => apiFetch<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'PATCH', body }),
  // DELETE carries a body here because the failed-data endpoint requires an explicit
  // `{confirm: true}` — a destructive action should not be triggerable by a bare URL.
  delete: <T>(path: string, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'DELETE' }),
}

/**
 * Multipart POST for the participant import (`/admin/participants/import[-send]`).
 *
 * Bypasses {@link apiFetch} because `FormData` must not carry an explicit `Content-Type`: the
 * browser has to set it itself so the multipart boundary is included. Everything else — the
 * envelope, the error mapping, the correlation id — is identical.
 */
export async function apiUpload<T>(
  path: string,
  form: FormData,
  options: Pick<RequestOptions, 'csrfToken' | 'requestId' | 'signal'> = {},
): Promise<T> {
  const requestId = options.requestId ?? newRequestId()
  const headers: Record<string, string> = { Accept: 'application/json', 'X-Request-ID': requestId }
  if (options.csrfToken) headers['X-CSRF-Token'] = options.csrfToken

  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers,
    body: form,
    credentials: 'same-origin',
    signal: options.signal,
  })

  let envelope: Envelope<T> | null = null
  try {
    envelope = (await response.json()) as Envelope<T>
  } catch {
    throw new ApiError({
      code: ErrorCodes.SERVICE_UNAVAILABLE,
      message: 'The service returned an unreadable response',
      status: response.status,
      requestId: response.headers.get('X-Request-ID') ?? requestId,
    })
  }

  if (!response.ok || envelope?.error) {
    throw new ApiError({
      code: envelope?.error?.code ?? ErrorCodes.INTERNAL_ERROR,
      message: envelope?.error?.message ?? 'Upload failed',
      status: response.status,
      requestId: envelope?.request_id ?? requestId,
      details: envelope?.error?.details,
    })
  }

  return envelope.data as T
}

/**
 * Downloads a streamed export and hands it to the browser as a file.
 *
 * `window.location.assign` was the obvious approach and is wrong here: a navigation cannot see
 * the response status, so a `409 CONFLICT` (the oversized-XLSX refusal, `M13-R8`) would appear to
 * the user as a download that simply never arrived. Fetching first means the refusal is a value
 * this function can throw — which the invitation export needs too, because a legacy participant
 * requiring a password reset comes back as a `409` the screen has to explain.
 */
export async function apiDownload(
  path: string,
  options: {
    fallbackFilename: string
    signal?: AbortSignal
    /** `POST` for the invitation export, whose selection travels in a body (S1 §5.3). */
    method?: 'GET' | 'POST'
    body?: unknown
    csrfToken?: string
  } = { fallbackFilename: 'download' },
): Promise<void> {
  const requestId = newRequestId()
  const headers: Record<string, string> = { 'X-Request-ID': requestId }
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'
  if (options.csrfToken) headers['X-CSRF-Token'] = options.csrfToken

  const response = await fetch(`${API_BASE}${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    credentials: 'same-origin',
    signal: options.signal,
  })

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as Envelope<unknown> | null
    throw new ApiError({
      code: body?.error?.code ?? ErrorCodes.INTERNAL_ERROR,
      message: body?.error?.message ?? 'The export could not be produced',
      status: response.status,
      requestId: body?.request_id ?? requestId,
    })
  }

  const disposition = response.headers.get('Content-Disposition') ?? ''
  const match = /filename="?([^";]+)"?/i.exec(disposition)
  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = match?.[1] ?? options.fallbackFilename
  document.body.append(anchor)
  anchor.click()
  anchor.remove()
  // Revoking immediately can cancel the download in some browsers; one tick is enough.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/** Builds a query string, dropping empty values so a blank filter is not sent as `?country=`. */
export function queryString(params: Record<string, unknown>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      for (const entry of value) search.append(key, String(entry))
    } else {
      search.set(key, String(value))
    }
  }
  const rendered = search.toString()
  return rendered ? `?${rendered}` : ''
}
